import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation24() {
  const { lesson, course, accent } = findLesson("piano-foundation", "24");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF24-How-To-Grow-Animated.mp4"
        caption={`Know · Hear · Play — the three dimensions, traced as a spiral that widens every loop`}
        hint={`≈ 22 sec · you never finish, you go deeper`}
      />
      <KeyHighlights>
        <Highlight>The <strong>three dimensions of music</strong>: <strong>Know</strong> (theory), <strong>Hear</strong> (ear training), <strong>Play</strong> (technique). Work all three — not just one.</Highlight>
        <Highlight>Growth is <strong>spiral</strong>, not linear. Revisit a chapter you "finished" two months ago and you'll find new layers in it. Like watching a great film and seeing what you missed.</Highlight>
        <Highlight>Keep a <strong>recording app open</strong>. When something interesting comes out of your hands, capture it. That's where your future songs come from.</Highlight>
        <Highlight>Don't wait until you're "ready" to be creative. Create <em>while</em> you learn — same way you don't read books to play badminton, you just play.</Highlight>
        <Highlight>Keep practising the three dimensions every week — even if a single session only touches one of them. The point is the rotation, not the perfection of any single one.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The three dimensions" title="Know · Hear · Play" accent={accent.hex}>
        <p>
          the Foundation course ends with the framework that holds all of it
          together. There are three things to grow simultaneously — never one at the expense
          of the others.
        </p>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">Know</div>
            <div className="mt-2 font-display text-xl font-bold text-nsm-ink">Theory</div>
            <p className="mt-2 text-[13px] text-nsm-dim">
              Scales, intervals, chords, progressions. The vocabulary that lets you
              understand what you're playing — and what you're hearing.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">Hear</div>
            <div className="mt-2 font-display text-xl font-bold text-nsm-ink">Ear training</div>
            <p className="mt-2 text-[13px] text-nsm-dim">
              Recognising intervals, scales, chord qualities, rhythms — by ear alone. The
              skill that lets you learn songs without sheet music in front of you.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">Play</div>
            <div className="mt-2 font-display text-xl font-bold text-nsm-ink">Technique</div>
            <p className="mt-2 text-[13px] text-nsm-dim">
              Finger control, hand independence, posture, dynamics, the physical execution
              of music. The dimension that turns ideas into sound.
            </p>
          </div>
        </div>

        <KeyIdea>
          Always work on three-dimensional playing. Theory, ear training, technique. If one
          falls behind, the other two start to feel limited too. They reinforce each other.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The spiral" title="You don't 'finish' a chapter — you revisit it" accent={accent.hex}>
        <Pull>
          All of our musical growth is a very spiral process. Right? So if you learn a
          chapter, if you learn the theory of the chapter, you could revisit it after
          maybe a month or two and you learn more things about the same chapter. Right?
          And the same thing with hearing. You start hearing things differently. It's
          like watching a great movie like The Godfather or something which you could
          watch like a million times and still not get bored.
        </Pull>

        <p>
          The chapters of this course aren't a one-pass road. Lessons 1 and 2 (notes on the
          piano) will mean different things to you in three months. The Hand Ratio lesson
          will read differently after you've played a few gigs. Pendulum will click in a new
          way once you've started playing scales seriously. Loop back; the lesson didn't
          change — you did.
        </p>
      </Insight>

      <Insight eyebrow="Many things at once" title="Music is rhythm, harmony, melody, creativity — all in motion" accent={accent.hex}>
        <p>
          The execution side of piano is itself multi-dimensional. You have to:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>Play a melody while holding a rhythm.</li>
          <li>Comp with chords across two hands.</li>
          <li>Manage rhythm, harmony, and melody simultaneously.</li>
          <li>Work on creativity — not just on copying what others have done.</li>
        </ul>

        <p>
          Each of those is its own skill. The Foundation course has touched every one of
          them at least once. From here, the work is to keep all of them in motion at once.
        </p>
      </Insight>

      <Insight eyebrow="Create while you learn" title="The badminton principle" accent={accent.hex}>
        <Pull>
          It's the same thing with sports. If you're a beginner level badminton player
          or a beginner level footballer, what do you do? You don't sit and read books.
          You actually play the game. Right? So that's the main approach which I wanted
          to convey throughout this entire course. There is tough stuff, but you need to
          play it and enjoy it. And unless it becomes fun for you and unless you've made
          it into a nice musical product, you should not stop.
        </Pull>

        <Tip>
          Keep a recording app on your phone, ready to go. Whenever you come up with
          something interesting on the keyboard — even a four-bar idea — record it. You
          never know which fragment becomes your next song. Don't wait until you're "good
          enough"; the habit of capturing material is what makes you good enough.
        </Tip>

        <Steps title="A weekly creative routine">
          <Step>
            One day a week, instead of practising, just <strong>improvise</strong>. Set a
            scale, set a rhythm pattern, and let your hands wander.
          </Step>
          <Step>
            <strong>Record</strong> the improvisation. Don't critique — just capture.
          </Step>
          <Step>
            Listen back the next morning. Notice the moments that stood out. Those are your
            ideas — keep them.
          </Step>
          <Step>
            Once a month, take one captured idea and try to <strong>shape it into a
            song</strong>. Add a verse, add a chorus. Even if it's two minutes long, that's a
            song.
          </Step>
        </Steps>
      </Insight>

      <Insight eyebrow="Where to keep growing" title="The rotation that pays dividends" accent={accent.hex}>
        <p>
          From here, growth comes from rotating through the three dimensions every week —
          a little Theory, a little Ear Training, a little Technique — rather than going
          deep on any single one for too long. A typical practice week might look like:
        </p>

        <ul className="mt-2 space-y-1 text-[13px] text-nsm-dim">
          <li><strong>Two days</strong> on technique drills (the Foundation drills you already know).</li>
          <li><strong>Two days</strong> on theory — write a scale, build its triads, sketch a progression.</li>
          <li><strong>Two days</strong> on ear training — work out a song by ear, transcribe a melody.</li>
          <li><strong>One day</strong> just to play — improvise, sing, capture ideas. No agenda.</li>
        </ul>

        <p className="mt-4 text-sm text-nsm-dim">
          Thanks for joining this course and all the best for the road ahead.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-25"
        label="Lesson 25 · Scales Ascending & Descending - White Note"
        why="The Foundation course technically ends here, but the next lessons on scales and chord shapes go deeper into the technique you'll need to keep growing."
      />
    </LessonShell>
  );
}
